import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';

import useSessionStore from '../../../../../hooks/useSessionStore';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { DateFormat } from '../../../../../utils/date';
import { openFirestoreDocument } from '../../../../../utils/support';
import CollectionName from '../../../../../utils/collections';
import LoadingPage from '../../../../../components/LoadingPage';
import WeeklyCheckin from '../../../../Model/WeeklyCheckin';
import User from '../../../../../Model/User';
import ClientWeightChartModal from '../../../ClientWeightChart';
import CheckInStats from '../../../CheckInStats';
import EmptyView from '../EmptyView';

import {
  Container,
  DateText,
  HeaderContainer,
  NavigationContainer,
  TextContainer,
  UpdatedText,
  StyledText,
  StyledButton,
  StyledOnCallActions,
} from './styles';
import texts from './texts.json';

const CheckInDetailsSlider = ({ user, userDoc, coachId }) => {
  const { isOnCallUser } = useSessionStore();

  const [weeklyCheckinDocs, setWeeklyCheckinDocs] = useState([]);
  const [selectedCheckinDocIndex, setSelectedCheckinDocIndex] = useState();
  const [isReady, setIsReady] = useState(false);
  const [showWeightHistoryModal, setShowWeightHistoryModal] = useState(false);
  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const init = async () => {
      const weeklyCheckinCollection = await WeeklyCheckin.getByUserAndCoach(user, coachId);
      const docs = weeklyCheckinCollection.hasDocs ? weeklyCheckinCollection.docs.slice() : [];
      if (isComponentMountedRef.current) {
        setWeeklyCheckinDocs(docs.reverse());
        if (docs.length > 0) {
          setSelectedCheckinDocIndex(docs.length - 1);
        }
        setIsReady(true);
      }
    };
    if (!isReady) {
      init();
    }
  }, [
    user,
    coachId,
    isReady,
    isComponentMountedRef,
  ]);

  const onNavigationClick = useCallback((forward = true) => {
    if (forward) {
      setSelectedCheckinDocIndex((prevIndex) => {
        if (prevIndex !== (weeklyCheckinDocs.length - 1)) {
          return prevIndex + 1;
        }
        return prevIndex;
      });
    } else {
      setSelectedCheckinDocIndex((prevIndex) => {
        if (prevIndex !== 0) {
          return prevIndex - 1;
        }
        return prevIndex;
      });
    }
  }, [
    weeklyCheckinDocs,
  ]);

  if (!isReady) {
    return <LoadingPage />;
  }

  if (weeklyCheckinDocs.length === 0) {
    return <EmptyView text={texts.emptyText} />;
  }

  return (
    <Container>
      <HeaderContainer>
        <TextContainer>
          <DateText>
            {`${weeklyCheckinDocs[selectedCheckinDocIndex]
              && moment(weeklyCheckinDocs[selectedCheckinDocIndex].updated)
                .format(DateFormat.MONTH_NAME_DATE_FORMAT)}`}
          </DateText>
          <UpdatedText>
            {`Updated ${weeklyCheckinDocs[selectedCheckinDocIndex]
              && moment(weeklyCheckinDocs[selectedCheckinDocIndex].updated)
                .format(DateFormat.DATE_FORMAT_TIME_MINUTES)}`}
          </UpdatedText>
        </TextContainer>
        <NavigationContainer>
          <StyledText>{`Week ${selectedCheckinDocIndex + 1} of ${weeklyCheckinDocs.length}`}</StyledText>
          <StyledButton onClick={() => onNavigationClick(false)}>
            <ArrowBack />
          </StyledButton>
          <StyledButton onClick={() => onNavigationClick()}>
            <ArrowForward />
          </StyledButton>
        </NavigationContainer>
      </HeaderContainer>
      {isOnCallUser && (
        <StyledOnCallActions
          actions={[
            {
              onClick: () => {
                openFirestoreDocument(
                  `${CollectionName.CHECK_IN}/${weeklyCheckinDocs[selectedCheckinDocIndex].checkInId}`,
                );
              },
              disabled: !weeklyCheckinDocs[selectedCheckinDocIndex],
              label: texts.support.checkin,
            },
          ]}
        />
      )}
      <CheckInStats
        activity={weeklyCheckinDocs[selectedCheckinDocIndex]}
        onWeightHistoryClick={() => setShowWeightHistoryModal(true)}
        clientUserDoc={userDoc}
      />
      {showWeightHistoryModal && (
        <ClientWeightChartModal
          showModal={showWeightHistoryModal}
          onClose={() => setShowWeightHistoryModal(false)}
          checkinDocs={weeklyCheckinDocs}
        />
      )}
    </Container>
  );
};

CheckInDetailsSlider.propTypes = {
  user: PropTypes.string.isRequired,
  coachId: PropTypes.string.isRequired,
  userDoc: PropTypes.instanceOf(User),
};

CheckInDetailsSlider.defaultProps = {
  userDoc: null,
};

export default CheckInDetailsSlider;
